<template>
  <div
    :class="cellClass"
    class="relative flex h-full min-h-[112px] flex-col gap-1 px-2 py-1.5 text-left"
    @click="onCellClick"
  >
    <!-- 空单元格 -->
    <template v-if="!node">
      <div class="flex flex-1 items-center justify-center text-[11px] text-slate-300">—</div>
    </template>

    <!-- 平台单元格 -->
    <template v-else-if="isPlatform">
      <div class="flex items-center gap-1.5">
        <span class="inline-block h-3 w-1 rounded-full bg-indigo-500"></span>
        <span class="status-badge bg-indigo-50 text-indigo-700">平台</span>
        <span class="truncate text-xs font-medium text-slate-700">{{ platformName }}</span>
      </div>
      <div class="mt-1 text-xs text-slate-500">
        品名：<span class="text-slate-700">{{ displayedEdgeProductName }}</span>
      </div>
      <button
        type="button"
        class="lock-chip mt-auto"
        v-permission="'enterprise-invoice:chain:lock'"
        :class="node.productNameLocked ? 'is-locked' : 'is-unlocked'"
        :disabled="syncing"
        title="锁定品名"
        @click.stop="emit('toggle-lock', 'productNameLocked')"
      >品</button>
    </template>

    <!-- 主体单元格 -->
    <template v-else>
      <div class="flex items-start justify-between gap-1">
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-semibold text-slate-900">{{ companyName }}</div>
          <div class="mt-0.5 flex flex-wrap items-center gap-1 text-[11px]">
            <span v-if="regionName" class="status-badge bg-slate-100 text-slate-600">{{ regionName }}</span>
            <span v-if="isHundredPercent" class="status-badge bg-amber-50 text-amber-700">100%</span>
          </div>
        </div>
        <button
          v-if="canRerankAfter"
          type="button"
          class="table-action-button is-purple shrink-0"
          v-permission="'enterprise-invoice:chain:generate'"
          title="从此节点后重排" aria-label="从此节点后重排"
          :disabled="syncing"
          @click.stop="emit('regenerate-after')"
        >
          <ArrowPathIcon class="h-3.5 w-3.5" />
        </button>
      </div>

      <p v-if="node.invalidReason" class="text-[11px] font-medium text-red-600">{{ node.invalidReason }}</p>

      <div class="flex items-baseline gap-1.5 text-xs">
        <span class="text-[10px] uppercase tracking-wide text-slate-400">进</span>
        <span class="font-medium text-slate-700">{{ formatInvoiceAmount(inputAmount) }}</span>
        <span class="text-slate-300">→</span>
        <span class="text-[10px] uppercase tracking-wide text-slate-400">出</span>
        <span class="font-semibold text-slate-900">{{ formatInvoiceAmount(outputAmount) }}</span>
      </div>
      <div class="flex items-baseline gap-2 text-[11px] text-slate-500">
        <span>
          <span class="text-[10px] uppercase tracking-wide text-slate-400">利率</span>
          <span class="ml-1 font-medium" :class="isHundredPercent ? 'text-amber-700' : 'text-slate-700'">{{ formatProfitRate(profitRate) }}</span>
        </span>
        <span class="text-slate-300">·</span>
        <span>
          <span class="text-[10px] uppercase tracking-wide text-slate-400">税</span>
          <span class="ml-1 font-medium text-slate-700">{{ formatInvoiceAmountTo(taxAmount, 2) }}</span>
        </span>
      </div>
      <div class="truncate text-[11px] text-slate-400">
        <span class="text-[10px] uppercase tracking-wide">品名</span>
        <span class="ml-1" :class="edgeProductUnmatched ? 'font-medium text-amber-700' : 'text-slate-500'">{{ displayedEdgeProductName }}</span>
        <span v-if="edgeProductUnmatched" class="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-700" title="R_PRODUCT_NAME_NOT_MATCHED">品名未匹配</span>
      </div>

      <div class="mt-auto flex flex-wrap gap-1">
        <button
          v-for="item in lockChips"
          :key="item.key"
          type="button"
          class="lock-chip"
          v-permission="'enterprise-invoice:chain:lock'"
          :class="item.locked ? 'is-locked' : 'is-unlocked'"
          :disabled="syncing"
          :title="item.title"
          @click.stop="emit('toggle-lock', item.key)"
        >{{ item.label }}</button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { ArrowPathIcon } from '@heroicons/vue/24/outline'
import { formatInvoiceAmount, formatInvoiceAmountTo, formatProfitRate } from '../utils'

const props = defineProps({
  node: { type: Object, default: null },
  regionName: { type: String, default: '' },
  issueMark: { type: Object, default: null },
  edgeProductName: { type: String, default: '' },
  edgeProductMatched: { type: Boolean, default: true },
  edgeProductItems: { type: Array, default: () => [] },
  syncing: { type: Boolean, default: false },
  isPlatform: { type: Boolean, default: false },
  canRerankAfter: { type: Boolean, default: false }
})

const emit = defineEmits(['edit', 'toggle-lock', 'regenerate-after'])

const companyName = computed(() => {
  const node = props.node
  if (!node) return ''
  return node.effectiveCompanyName || node.companyNameSnapshot || node.companyName || '-'
})

const platformName = computed(() => props.node?.effectiveCompanyName || '平台')

const inputAmount = computed(() => props.node?.effectiveInputAmount ?? props.node?.inputAmount ?? null)
const outputAmount = computed(() => props.node?.effectiveOutputAmount ?? props.node?.outputAmount ?? null)
const profitRate = computed(() => props.node?.effectiveProfitRate ?? props.node?.profitRate ?? null)

// 利润金额 = 销项 - 进项；税额 = 销项*0.06 - 进项*0.06（= 利润金额 * 0.06）
const profitAmount = computed(() => {
  const input = Number(inputAmount.value)
  const output = Number(outputAmount.value)
  if (!Number.isFinite(input) || !Number.isFinite(output)) return null
  return output - input
})

const taxAmount = computed(() => {
  const profit = profitAmount.value
  if (profit === null || !Number.isFinite(profit)) return null
  return profit * 0.06
})

const formattedEdgeProductItems = computed(() => formatEdgeProductItems(props.edgeProductItems))
const edgeProductUnmatched = computed(() =>
  props.edgeProductMatched === false ||
  props.edgeProductItems.some(item => item?.matched === false || item?.productNameMatched === false)
)
const displayedEdgeProductName = computed(() =>
  formattedEdgeProductItems.value || props.edgeProductName || props.node?.productName || '—'
)

const isHundredPercent = computed(() => {
  const rate = Number(profitRate.value)
  if (!Number.isFinite(rate)) return false
  return Number(rate.toFixed(6)) === 1
})

const lockChips = computed(() => {
  const node = props.node || {}
  return [
    { key: 'companyLocked', label: '公', locked: Boolean(node.companyLocked), title: '锁定公司' },
    { key: 'inputAmountLocked', label: '进', locked: Boolean(node.inputAmountLocked), title: '锁定进项' },
    { key: 'outputAmountLocked', label: '出', locked: Boolean(node.outputAmountLocked), title: '锁定销项' },
    { key: 'profitRateLocked', label: '利', locked: Boolean(node.profitRateLocked), title: '锁定利润率' },
    { key: 'productNameLocked', label: '品', locked: Boolean(node.productNameLocked), title: '锁定品名' }
  ]
})

const cellClass = computed(() => {
  if (!props.node) return ['border border-dashed border-slate-200 bg-slate-50/30']
  const classes = ['border cursor-default transition-colors hover:bg-slate-50/60']
  if (props.issueMark?.hasError) {
    classes.push('border-red-300 bg-red-50/50')
  } else if (props.issueMark?.hasWarning || edgeProductUnmatched.value) {
    classes.push('border-amber-300 bg-amber-50/40')
  } else if (props.node.invalidReason) {
    classes.push('border-red-200 bg-red-50/30')
  } else if (props.isPlatform) {
    classes.push('border-indigo-200 bg-indigo-50/30')
  } else {
    classes.push('border-slate-200 bg-white')
  }
  return classes
})

function formatEdgeProductItems(items = []) {
  return (items || [])
    .map(item => {
      const rawName = item?.productName || item?.productNames || ''
      const name = Array.isArray(rawName) ? rawName.join('/') : String(rawName || '').trim()
      if (!name) return ''
      const amount = item?.invoiceAmount ?? item?.amount ?? item?.displayAmount
      const amountText = formatInvoiceAmount(amount)
      return amountText === '-' ? name : `${name} ${amountText}`
    })
    .filter(Boolean)
    .join('、')
}

function onCellClick() {
  if (props.syncing || !props.node || props.isPlatform) return
  emit('edit')
}
</script>

<style scoped>
.lock-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  line-height: 1;
  border: 1px solid rgb(203 213 225);
  background: #fff;
  color: rgb(100 116 139);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.lock-chip:hover:not(:disabled) {
  border-color: rgb(148 163 184);
}
.lock-chip.is-locked {
  background: rgb(236 253 245);
  border-color: rgb(110 231 183);
  color: rgb(4 120 87);
  font-weight: 600;
}
.lock-chip.is-unlocked {
  background: rgb(241 245 249);
  color: rgb(148 163 184);
}
.lock-chip:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
